import { PusherEvent } from '@mondaygpt/constants'
import { selectBusinesses } from 'modules/business'
import { useAppSelector } from 'modules/core'
import { useListenToPusherEvent } from 'modules/core/hooks/useListenToPusherEvent'
import { useEffect, useMemo, useState } from 'react'
import { ReferralClaimDialog } from './ReferralClaimDialog'
import { claimableDismissKey } from './use-claim-referral-reward'
import { useReferralProgram } from './use-referral-program'

/** localStorage key for "user dismissed the multi-biz picker for this set of rewards". */
const DISMISS_KEY_PREFIX = 'referral-claim-dismissed:'

/**
 * Read the localStorage dismissed-flag safely. SSR-safe (returns false when
 * `window` is unavailable) and swallows QuotaExceeded/private-mode failures —
 * a dismissed flag we can't read just means the dialog re-pops, which is a
 * softer failure than the app crashing.
 */
const wasDismissed = (key: string): boolean => {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(DISMISS_KEY_PREFIX + key) === '1'
  } catch {
    return false
  }
}

const markDismissed = (key: string): void => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(DISMISS_KEY_PREFIX + key, '1')
  } catch (err) {
    // Non-fatal: means the dialog will re-pop on next load. Log so a broken
    // storage backend is visible without breaking the celebration.
    console.warn('Failed to persist referral-claim dismissed flag:', err)
  }
}

/**
 * Global mount that reacts to approved-but-unclaimed referral rewards by
 * auto-opening a T3 celebration dialog:
 *
 * - 1 business → celebration modal with NO picker; one Claim button drops the
 *   credits into their only business.
 * - 2+ businesses → the same modal plus a business picker; user picks + claims.
 * - 0 businesses → nothing yet; the reward waits (stays claimable) until they
 *   have a business to claim into.
 *
 * Any dismiss (Esc / overlay / X / "I'll decide later") is persisted per id-set
 * so it doesn't re-pop; the sidebar dot + `/referrals` waiting card remain.
 *
 * Also refreshes /me when the `ReferralRewardApproved` Pusher event fires so
 * the celebration surfaces live without a page reload. If the user was offline
 * when the reward landed, `/me` on next app load already carries `claimable`
 * populated — the Pusher event is purely the live-refresh path.
 *
 * Renders `null` when the referral program is off or there's nothing to claim.
 */
export const ReferralRewardCelebration = () => {
  const { enabled, info, refetch } = useReferralProgram()
  const businesses = useAppSelector(selectBusinesses)

  const claimable = useMemo(() => info?.claimable ?? [], [info?.claimable])
  // Cheap pure string join — no memo needed; it's value-stable across renders.
  const dismissKey = claimableDismissKey(claimable)

  const [dialogOpen, setDialogOpen] = useState(false)

  // Live-refresh path: a NEW approval arrives → refetch /me so `claimable`
  // updates and the effect below re-runs against the new id-set.
  useListenToPusherEvent(PusherEvent.ReferralRewardApproved, () => {
    // Fire-and-forget; refetch reports its own errors.
    void refetch()
  })

  // Auto-open the celebration dialog whenever there's an unclaimed reward and
  // the user has at least one business to claim into. The dialog adapts to the
  // business count (picker vs. no picker). Skips when dismissed for THIS id-set,
  // and closes automatically when `claimable` empties (post-claim refetch) or
  // when there's no business yet (a business-less referrer waits).
  useEffect(() => {
    if (!enabled || claimable.length === 0 || businesses.length < 1) {
      setDialogOpen(false)
      return
    }
    if (wasDismissed(dismissKey)) return
    setDialogOpen(true)
  }, [enabled, claimable, businesses, dismissKey])

  const handleClose = () => {
    // Any close path (Esc, overlay click, X, "I'll decide later") counts as
    // "decide later" — persist the dismissal so it doesn't re-pop next mount.
    // A successful claim empties `claimable` first, which changes the key, so
    // this write happens against the now-obsolete key and doesn't affect
    // future approvals.
    markDismissed(dismissKey)
    setDialogOpen(false)
  }

  return <ReferralClaimDialog open={dialogOpen} onClose={handleClose} rewards={claimable} />
}
